import { MenuRoleDTO, ProductForSaleDTO, TicketLineDTO, MenuForSaleDTO, ProductGroupForSaleDTO, TicketDTO } from 'core/api';

export type ProductsByRoleName = Readonly<Record<string, ProductForSaleDTO[]>>;

export type Composition = Readonly<{
	currentRole: MenuRoleDTO;
	menuHeader: TicketLineDTO;
	currentRoleItemsCount: number;
	roles: MenuRoleDTO[];
	productsByRoleName: ProductsByRoleName;
}>;

type GetProductsFromProductGroupFn = (group: ProductGroupForSaleDTO, ticketId: number) => Promise<ProductForSaleDTO[]>;
type GetTicketFn = () => TicketDTO;
type AddProductFn = (composition: Composition, product: ProductForSaleDTO, quantity: number) => Promise<Result>;

type MenuComposerParams = {
	menu: MenuForSaleDTO,
	menuHeader: TicketLineDTO
};

export function getCurrentProducts(composition: Composition): ProductForSaleDTO[] {
	return composition.productsByRoleName[composition.currentRole.Name] ?? [];
}

export function getItemsCountByRole(role: MenuRoleDTO, menuHeader: TicketLineDTO, getTicket: GetTicketFn) {
	return getTicket().Lines
		.filter(x => x.Product.GroupName === role.Name && x.OwnerId === menuHeader.Id)
		.sum(l => l.Quantity);
}

// Carga (si falta) productos del group actual y los mete en productsByCategoryId
async function ensureProductsForGroup(
	composition: Composition,
	currentRole: MenuRoleDTO,
	getTicket: GetTicketFn,
	getProducts: GetProductsFromProductGroupFn
): Promise<Composition> {

	const cached = composition.productsByRoleName[currentRole.Name];
	if (cached)
		return composition;

	const products = await getProducts(currentRole.Category, getTicket().Id);
	const nextComposition: Composition = {
		...composition,
		productsByRoleName: { ...composition.productsByRoleName, [currentRole.Name]: products }
	};

	return nextComposition;
}

export async function build(params: MenuComposerParams, getTicket: GetTicketFn, getProducts: GetProductsFromProductGroupFn): Promise<Composition> {
	return buildWithCurrentRole(params, params.menu.Roles[0], getTicket, getProducts);
}

export async function buildWithCurrentRole(
	params: MenuComposerParams,
	currentRole: MenuRoleDTO,
	getTicket: GetTicketFn,
	getProducts: GetProductsFromProductGroupFn
): Promise<Composition> {

	const menuHeader = params.menuHeader;

	const base: Composition = {
		currentRole,
		menuHeader,
		currentRoleItemsCount: getItemsCountByRole(currentRole, menuHeader, getTicket),
		roles: params.menu.Roles,
		productsByRoleName: {}
	};

	const composition = await ensureProductsForGroup(base, currentRole, getTicket, getProducts);
	return composition;
}

export async function ensureProductsForAllRoles(
	composition: Composition,
	getTicket: GetTicketFn,
	getProducts: GetProductsFromProductGroupFn
): Promise<Composition> {

	let next = composition;
	for (const role of composition.roles) {
		next = await ensureProductsForGroup(next, role, getTicket, getProducts);
	}

	return next;
}

export async function selectRole(
	composition: Composition,
	role: MenuRoleDTO,
	getTicket: GetTicketFn,
	getProducts: GetProductsFromProductGroupFn
): Promise<Composition> {

	const currentRole = role;

	const nextComposition = await ensureProductsForGroup(composition, currentRole, getTicket, getProducts);

	return {
		...nextComposition,
		currentRole,
		currentRoleItemsCount: getItemsCountByRole(currentRole, nextComposition.menuHeader, getTicket),
	};
}

export async function refresh(composition: Composition, getTicket: GetTicketFn, getProducts: GetProductsFromProductGroupFn): Promise<Composition> {
	return selectRole(composition, composition.currentRole, getTicket, getProducts);
}

// Helper opcional para el caso EXCLUDE_SIZE_AND_COLOR_PRODUCTS (sin mutar cache)
export function getCurrentProductsFiltered(
	composition: Composition,
	excludeSizeAndColorProducts?: 'EXCLUDE_SIZE_AND_COLOR_PRODUCTS'
): ProductForSaleDTO[] {
	const products = getCurrentProducts(composition);
	return excludeSizeAndColorProducts === undefined
		? products
		: products.filter(x => !x.IsSoldBySizeAndColor);
}

export async function getNextRole(composition: Composition, getTicket: GetTicketFn, getProducts: GetProductsFromProductGroupFn): Promise<Composition> {
	const idx = Math.min(composition.roles.indexOf(composition.currentRole) + 1, composition.roles.length - 1);
	return selectRole(composition, composition.roles[idx], getTicket, getProducts);
}

export function getRoleTotalQuantity(role: MenuRoleDTO, menuHeader: TicketLineDTO) {
	return role.MaxItems * menuHeader.Quantity;
}

export function getCurrentRolePendingQuantity({ currentRole, currentRoleItemsCount, menuHeader }: Composition): number {
	return getRoleTotalQuantity(currentRole, menuHeader) - currentRoleItemsCount;
}

function isRoleCompleted(role: MenuRoleDTO, menuHeader: TicketLineDTO, getTicket: GetTicketFn): boolean {
	return getTicket().Lines
		.filter(x => x.OwnerId === menuHeader.Id && x.Product.GroupName === role.Name)
		.sum(l => l.Quantity) >= getRoleTotalQuantity(role, menuHeader);
}

export async function getNextNotCompletedRole(
	composition: Composition,
	getTicket: GetTicketFn,
	getProducts: GetProductsFromProductGroupFn
): Promise<Composition> {

	const initialRole = composition.currentRole;
	let nextRole = initialRole;
	let finish = false;

	while (isRoleCompleted(nextRole, composition.menuHeader, getTicket) && !finish) {
		nextRole = composition.roles[(composition.roles.indexOf(nextRole) + 1) % composition.roles.length];
		finish = nextRole.Name === initialRole.Name;
	}

	return selectRole(composition, nextRole, getTicket, getProducts);
}

export function newMenuItemCanBeAdded(composition: Composition, quantity: number, getTicket: GetTicketFn): boolean {
	return !isRoleCompleted(composition.currentRole, composition.menuHeader, getTicket) && quantity <= getCurrentRolePendingQuantity(composition);
}

export function isCompleted(composition: Composition, getTicket: GetTicketFn): boolean {
	return composition.roles.every(role => isRoleCompleted(role, composition.menuHeader, getTicket));
}

export async function tryToComplete(
	composition: Composition,
	getTicket: GetTicketFn,
	getProducts: GetProductsFromProductGroupFn,
	addProduct: AddProductFn
): Promise<Composition> {

	const roles = [...composition.roles];
	while (roles.length) {
		const role = roles.shift()!;

		// Al autocompletar no puedo meter productos con talla y color
		composition = await selectRole(composition, role, getTicket, getProducts);

		const products = getCurrentProductsFiltered(composition, 'EXCLUDE_SIZE_AND_COLOR_PRODUCTS');
		if (products.length !== 1)
			return composition;

		const quantityToOrder = getCurrentRolePendingQuantity(composition);
		if (quantityToOrder <= 0)
			return composition;

		const result = await addProduct(composition, products[0], quantityToOrder);
		if (!result.ok)
			return composition;
	}

	return composition;
}
